import { useState, useEffect, useRef } from 'react';
import { datasets as datasetsApi } from '../api/client';

const CHUNK_SIZE = 10000;

async function loadAllCandles(dsId) {
  const dsRes = await datasetsApi.get(dsId);
  const ds = dsRes.data;
  const total = ds.bar_count;
  const allCandles = [];

  for (let offset = 0; offset < total; offset += CHUNK_SIZE) {
    const res = await datasetsApi.candles(dsId, offset, CHUNK_SIZE);
    for (const c of res.data.candles) {
      allCandles.push({
        time: Math.floor(new Date(c.timestamp).getTime() / 1000),
        open: parseFloat(c.open),
        high: parseFloat(c.high),
        low: parseFloat(c.low),
        close: parseFloat(c.close),
        volume: parseFloat(c.volume),
      });
    }
    if (res.data.candles.length < CHUNK_SIZE) break;
  }

  const timeMap = new Map();
  for (const candle of allCandles) {
    timeMap.set(candle.time, candle);
  }

  return { instrument: ds.instrument, timeframe: ds.timeframe, allCandles, timeMap };
}

export default function useCorrelationPairs(primaryVisibleCandles, correlationDatasetIds) {
  const [pairData, setPairData] = useState({});
  const [syncedPairs, setSyncedPairs] = useState({});
  const loadedRef = useRef(new Set());
  const pairDataRef = useRef({});

  // Load candles for each correlation dataset
  useEffect(() => {
    if (!correlationDatasetIds || correlationDatasetIds.length === 0) {
      setPairData({});
      pairDataRef.current = {};
      return;
    }

    for (const dsId of correlationDatasetIds) {
      if (loadedRef.current.has(dsId)) continue;
      loadedRef.current.add(dsId);

      loadAllCandles(dsId).then(data => {
        console.log(`[Correlation] Loaded ${data.instrument}: ${data.allCandles.length} candles, timeMap size: ${data.timeMap.size}`);
        pairDataRef.current = { ...pairDataRef.current, [dsId]: data };
        setPairData(prev => ({ ...prev, [dsId]: data }));
      }).catch(err => {
        console.error(`Failed to load correlation dataset ${dsId}:`, err);
        loadedRef.current.delete(dsId);
      });
    }

    // Clean removed pairs
    const idsSet = new Set(correlationDatasetIds.map(String));
    for (const key of Object.keys(pairDataRef.current)) {
      if (!idsSet.has(key)) {
        delete pairDataRef.current[key];
        loadedRef.current.delete(parseInt(key));
      }
    }
  }, [correlationDatasetIds]);

  // Sync correlation candles to primary visible candles — runs on every render update
  useEffect(() => {
    const pairs = pairDataRef.current;
    if (!primaryVisibleCandles?.length || Object.keys(pairs).length === 0) {
      setSyncedPairs({});
      return;
    }

    const primaryTimes = primaryVisibleCandles.map(c => c.time);
    const result = {};

    for (const [dsId, data] of Object.entries(pairs)) {
      const synced = [];
      for (const t of primaryTimes) {
        const candle = data.timeMap.get(t);
        if (candle) synced.push(candle);
      }
      console.log(`[Correlation] Sync ${data.instrument}: ${synced.length}/${primaryTimes.length} bars matched`);
      result[dsId] = {
        instrument: data.instrument,
        timeframe: data.timeframe,
        candles: synced,
      };
    }

    setSyncedPairs(result);
  }, [primaryVisibleCandles, pairData]);

  return syncedPairs;
}
